import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";

import routes from "../../routes";
import {
  attemptGetUploadUrl,
  attemptGetStatus
} from "../../store/thunks/order";
import ReactS3Uploader from "react-s3-uploader";

import { ReactComponent as IconCheck } from "../../images/icon_check.svg";

const Guestlist = ({ history }) => {
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const [uploadProgress, setUploadProgress] = useState(0);
  const [success, setSuccess] = useState(false);
  const [intervalInstance, setIntervalInstance] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [clear, setClear] = useState(false);

  const status = useSelector(({ order }) => order.status);
  const orderId = useSelector(({ order }) => order.data.id);
  const dispatch = useDispatch();

  // Check if there's existing status at initialization
  useEffect(() => {
    // If Redux state exists display the current status
    if (status) {
      updateStatus(status).then(() => setInitialLoading(false));
    }

    // If Redux state doesn't exist get status
    if (!status && orderId) {
      getCurrentStatus();
    }
  }, [orderId, status]);

  // After upload get status
  useEffect(() => {
    if (submitted && clear) {
      getCurrentStatus();
    }
  }, [intervalInstance, clear]);

  // Display the error state
  const updateOnError = async () => {
    return new Promise((resolve, reject) => {
      if (intervalInstance) {
        clearInterval(intervalInstance);
      }

      setUploadProgress(0);
      setErrors({
        ...errors,
        upload: true
      });
      setLoading(false);

      resolve("Error state displayed");
    });
  };

  // Display the current status
  const updateStatus = status => {
    return new Promise(async (resolve, reject) => {
      if (["PENDING_PAYMENT", "AWAITING_MAILING", "MAILED"].includes(status)) {
        if (intervalInstance) {
          clearInterval(intervalInstance);
        }

        setUploadProgress(100);
        setSuccess(true);
        setLoading(false);
        resolve("Current status displayed");
      } else if (status === "GUEST_LIST_PROCESSING_FAILED") {
        await updateOnError();
        resolve("Current status displayed");
      } else {
        if (submitted & clear) {
          // Ping the server every 1s until the processing is done
          setClear(false);
          setTimeout(() => {
            setClear(true);
          }, 1000);
        }
        resolve("Status in progress; retrying");
      }
      reject("Status not displayed");
    });
  };

  // Get the current status (http request)
  const getCurrentStatus = async () => {
    await dispatch(attemptGetStatus(orderId))
      .then(async res => {
        await updateStatus(res.status).then(() => setInitialLoading(false));
      })
      .catch(async () => {
        await updateOnError();
      });
  };

  // Get the amazon s3 signed url (http request)
  const getSignedUrl = async (file, callback) => {
    setErrors({});
    setLoading(true);
    setSubmitted(true);
    await dispatch(attemptGetUploadUrl(orderId))
      .then(url => {
        callback({
          signedUrl: url
        });
      })
      .catch(error => {
        if (error.response && error.response.status === 400) {
          setErrors({
            ...errors,
            server: error.response.data.message
          });
          setUploadProgress(0);
          setLoading(false);
        }
      });
  };

  // Progress bar
  const startInterval = (currentProgress, step, uploadProgress) => {
    return new Promise((resolve, reject) => {
      if (uploadProgress < 100) {
        const interval = setInterval(() => {
          currentProgress += step;
          let progress = Math.round(
            (Math.atan(currentProgress) / (Math.PI / 2)) * 100
          );
          setUploadProgress(progress);
          if (progress >= 70) {
            step = 0.1;
          }
        }, 100);
        resolve(interval);
      } else {
        resolve("clear");
      }
      reject("Interval error");
    });
  };

  // Start the progress bar on upload submit
  const onProgress = async uploadProgress => {
    const interval = await startInterval(0, 1, uploadProgress);
    if (interval !== "clear") {
      setIntervalInstance(interval);
    }
  };

  // Trigger get status
  const onFinish = () => {
    setClear(true);
  };

  const onError = error => {
    updateOnError();
  };

  // Next button is displayed only if the upload succeed
  const goNext = e => {
    e.preventDefault();
    // Go to the "Finish order" page
    history.push(routes.order);
  };

  // Clear upload state
  const handleRedoButton = () => {
    setUploadProgress(0);
    setSuccess(false);
    setSubmitted(false);
  };

  if (initialLoading) {
    return <div className="dots dots-primary"></div>;
  }

  return (
    <>
      <h1 className="mb-2">Guestlist</h1>
      <p className="mb-lg w-75 text-center">
          Upload your guest list here! Guest lists need to be in either Excel or CSV format and have a maximum size of 10mb.
          Make sure to include a column with your guests' names and another with their addresses.
      </p>
      <div
        className={`upload-wrapper d-flex flex-row justify-content-between align-items-center p-2 mb-md ${errors.upload &&
          "upload-error"}`}
      >
        <p className="upload-text mb-0">
          {Object.entries(errors).length === 0 && !loading && !success ? (
            <>upload guest list</>
          ) : null}
          {errors.upload && <>upload error</>}
          {loading && (
            <>
              processing{" "}
              <span className="text-secondary">({uploadProgress}%)</span>
            </>
          )}
        </p>
        {!loading && !success ? (
          <div className="upload-btn-wrapper">
            <ReactS3Uploader
              getSignedUrl={getSignedUrl}
              accept=".xlsx, .csv"
              onProgress={onProgress}
              onError={onError}
              onFinish={onFinish}
              uploadRequestHeaders={{
                "x-amz-acl": "bucket-owner-full-control"
              }}
              contentDisposition="auto"
              disabled={loading}
            />
            <button
              type="button"
              className="btn btn-primary"
              disabled={loading}
            >
              {Object.entries(errors).length === 0 && <>UPLOAD</>}
              {errors.upload && <>TRY AGAIN</>}
            </button>
          </div>
        ) : null}
        {loading && <div className="dots dots-primary"></div>}
        {success && (
          <div className="icon-round">
            <IconCheck />
          </div>
        )}
        <div
          className={`upload-progress flex-row justify-content-between align-items-center p-2 ${
            uploadProgress > 0 ? "d-flex" : "d-none"
          }`}
          style={{ width: `${uploadProgress}%` }}
        >
          <p
            className={`upload-text mb-0 ${
              uploadProgress > 0 ? "d-inline-block" : "d-none"
            }`}
          >
            {!success ? (
              <>processing ({uploadProgress}%)</>
            ) : (
              <>upload complete</>
            )}
          </p>
        </div>
      </div>
      {success && (
        <form noValidate onSubmit={goNext}>
          <button
            type="button"
            className="btn btn-secondary text-light mx-2"
            onClick={handleRedoButton}
          >
            REDO
          </button>
          <button type="submit" className="btn btn-primary mx-2">
            NEXT
          </button>
        </form>
      )}
    </>
  );
};

export default Guestlist;
